/**
 * Schedule animation (Recurring empty-state visual).
 *
 * SVG + CSS animation showing three stacked scheduled items with
 * dates and amounts. A small clock ticks at the top. On each
 * cycle: the top item "fires" (a green checkmark fades over it),
 * then slides out as the list shifts up and a new upcoming item
 * slides in from the bottom. Infinite 4.5-second loop.
 *
 * Metaphor: Recurring templates post transactions on their
 * schedule automatically.
 *
 * Reused by:
 *   - RecurringEmptyState (first-visit, no templates yet)
 *   - Potentially the Recurring Builder drawer intro (PR 11)
 *
 * Pure presentational, no props-driven state. CSS keyframes run
 * on the GPU so the animation is cheap. Honors
 * prefers-reduced-motion for accessibility.
 *
 * Why its own component (not inlined): the animation is reusable
 * and should not be confused with VelaSortAnimation (Rules) or
 * the Accounting Overview book animation. Each page gets its own
 * metaphor so visual language stays distinct across the Accounting
 * module.
 */

import type { CSSProperties } from "react";

interface ScheduleAnimationProps {
  /** Pixel width (height scales proportionally). Default 190. */
  width?: number;
}

const PALETTE = {
  ink: "#0E1A1A",
  tealDark: "#0F6E56",
  vela: "#6B46C1",
  line: "#D8DEE6",
  bgFade: "#F3F5F5",
};

const ASPECT_RATIO = 140 / 190;

export function ScheduleAnimation({ width = 190 }: ScheduleAnimationProps) {
  const height = Math.round(width * ASPECT_RATIO);

  const wrapStyle: CSSProperties = {
    width,
    height,
    display: "block",
    margin: "0 auto",
    position: "relative",
  };

  return (
    <div style={wrapStyle} aria-hidden="true">
      <style>{ANIMATION_CSS}</style>
      <svg
        viewBox="0 0 190 140"
        width={width}
        height={height}
        style={{ display: "block", overflow: "visible" }}
      >
        {/* Clock in the top center */}
        <g transform="translate(85, 10)">
          <circle className="sa-clock-face" cx="10" cy="10" r="8" />
          <circle className="sa-clock-center" cx="10" cy="10" r="1.5" />
          <line className="sa-clock-hand" x1="10" y1="10" x2="10" y2="4" />
        </g>

        {/* Item 1 (top, fires this cycle) */}
        <g className="sa-item sa-item-1" transform="translate(0, 32)">
          <rect className="sa-item-bg" x="10" y="0" width="170" height="28" rx="5" />
          <rect className="sa-date-pill" x="18" y="6" width="34" height="16" rx="3" />
          <text className="sa-date-text" x="35" y="16" textAnchor="middle">
            OCT 25
          </text>
          <rect className="sa-item-line" x="58" y="7" width="68" height="4" rx="2" />
          <rect className="sa-item-sub" x="58" y="15" width="45" height="3" rx="1.5" />
          <text className="sa-amount" x="172" y="18" textAnchor="end">
            $29.00
          </text>
          <rect className="sa-check-bg" x="10" y="0" width="170" height="28" rx="5" />
          <path className="sa-check-mark" d="M85 10 L92 17 L105 4" />
        </g>

        {/* Item 2 (middle) */}
        <g className="sa-item sa-item-2" transform="translate(0, 68)">
          <rect className="sa-item-bg" x="10" y="0" width="170" height="28" rx="5" />
          <rect className="sa-date-pill" x="18" y="6" width="34" height="16" rx="3" />
          <text className="sa-date-text" x="35" y="16" textAnchor="middle">
            OCT 31
          </text>
          <rect className="sa-item-line" x="58" y="7" width="68" height="4" rx="2" />
          <rect className="sa-item-sub" x="58" y="15" width="55" height="3" rx="1.5" />
          <text className="sa-amount" x="172" y="18" textAnchor="end">
            $3,842
          </text>
        </g>

        {/* Item 3 (bottom) */}
        <g className="sa-item sa-item-3" transform="translate(0, 104)">
          <rect className="sa-item-bg" x="10" y="0" width="170" height="28" rx="5" />
          <rect className="sa-date-pill" x="18" y="6" width="34" height="16" rx="3" />
          <text className="sa-date-text" x="35" y="16" textAnchor="middle">
            NOV 1
          </text>
          <rect className="sa-item-line" x="58" y="7" width="68" height="4" rx="2" />
          <rect className="sa-item-sub" x="58" y="15" width="42" height="3" rx="1.5" />
          <text className="sa-amount" x="172" y="18" textAnchor="end">
            $4,200
          </text>
        </g>

        {/* Item 4 (slides in from below after item 1 fires) */}
        <g className="sa-item sa-item-4" transform="translate(0, 104)">
          <rect className="sa-item-bg" x="10" y="0" width="170" height="28" rx="5" />
          <rect className="sa-date-pill" x="18" y="6" width="34" height="16" rx="3" />
          <text className="sa-date-text" x="35" y="16" textAnchor="middle">
            NOV 5
          </text>
          <rect className="sa-item-line" x="58" y="7" width="68" height="4" rx="2" />
          <rect className="sa-item-sub" x="58" y="15" width="48" height="3" rx="1.5" />
          <text className="sa-amount" x="172" y="18" textAnchor="end">
            $45.00
          </text>
        </g>
      </svg>
    </div>
  );
}

/**
 * Scoped CSS. Inlined via <style> inside the component so the
 * animation does not require importing a .css file or touching
 * the global stylesheet. Class names are prefixed `sa-` to avoid
 * collision with other animations (VelaSortAnimation uses `vsa-`).
 */
const ANIMATION_CSS = `
  .sa-item-bg { fill: #ffffff; stroke: ${PALETTE.line}; stroke-width: 1; }
  .sa-item-line { fill: ${PALETTE.line}; }
  .sa-item-sub { fill: ${PALETTE.line}; opacity: 0.5; }
  .sa-date-pill { fill: ${PALETTE.bgFade}; }
  .sa-date-text {
    fill: ${PALETTE.ink};
    font-size: 7px;
    font-weight: 700;
    font-family: -apple-system, "Segoe UI", Roboto, sans-serif;
  }
  .sa-amount {
    fill: ${PALETTE.ink};
    font-size: 7.5px;
    font-weight: 700;
    font-family: ui-monospace, Menlo, monospace;
  }

  .sa-check-bg { fill: ${PALETTE.tealDark}; opacity: 0; }
  .sa-check-mark {
    stroke: #ffffff;
    stroke-width: 2;
    fill: none;
    stroke-linecap: round;
    stroke-linejoin: round;
    opacity: 0;
  }

  .sa-clock-face { fill: #ffffff; stroke: ${PALETTE.vela}; stroke-width: 1.5; }
  .sa-clock-center { fill: ${PALETTE.vela}; }
  .sa-clock-hand {
    stroke: ${PALETTE.vela};
    stroke-width: 1.5;
    stroke-linecap: round;
    transform-origin: 95px 20px;
    transform-box: fill-box;
  }

  @keyframes sa-check-fade {
    0%, 50% { opacity: 0; }
    58%, 72% { opacity: 1; }
    80%, 100% { opacity: 0; }
  }
  @keyframes sa-slide-up {
    0%, 72% { transform: translateY(0); }
    80%, 100% { transform: translateY(-36px); }
  }
  @keyframes sa-slide-in {
    0%, 72% { transform: translateY(36px); opacity: 0; }
    80%, 100% { transform: translateY(0); opacity: 1; }
  }
  @keyframes sa-clock-tick {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .sa-item-1 { animation: sa-slide-up 4.5s ease-in-out infinite; }
  .sa-item-1 .sa-check-bg { animation: sa-check-fade 4.5s ease-in-out infinite; }
  .sa-item-1 .sa-check-mark { animation: sa-check-fade 4.5s ease-in-out infinite; }
  .sa-item-2 { animation: sa-slide-up 4.5s ease-in-out infinite; }
  .sa-item-3 { animation: sa-slide-up 4.5s ease-in-out infinite; }
  .sa-item-4 { animation: sa-slide-in 4.5s ease-in-out infinite; }
  .sa-clock-hand { animation: sa-clock-tick 4.5s linear infinite; }

  @media (prefers-reduced-motion: reduce) {
    .sa-item-1,
    .sa-item-2,
    .sa-item-3,
    .sa-item-4,
    .sa-clock-hand,
    .sa-check-bg,
    .sa-check-mark {
      animation: none;
    }
    .sa-item-4 { opacity: 0; }
  }
`;